import { useEffect, useRef } from "react";

type PageViewHandler = (isCancelled: () => boolean) => void | Promise<void>;

type UsePageViewAfterLoadArgs = {
  onPageView: PageViewHandler;
  triggerKey: string | undefined;
};

/**
 * Invokes `onPageView` after `document` load when needed so Segment `page()`
 * runs once the page has finished loading and flag middleware is synced.
 * Passes `isCancelled` so async work (e.g. BotID) can drop a stale fire after
 * awaiting when the route has already changed.
 */
function usePageViewAfterLoad({
  onPageView,
  triggerKey,
}: UsePageViewAfterLoadArgs): void {
  const onPageViewRef = useRef(onPageView);

  useEffect(() => {
    onPageViewRef.current = onPageView;
  }, [onPageView]);

  useEffect(() => {
    if (triggerKey === undefined) {
      return;
    }

    let cancelled = false;
    const isCancelled = () => cancelled;

    const firePageView = () => {
      if (cancelled) {
        return;
      }

      void onPageViewRef.current(isCancelled);
    };

    if (document.readyState === "complete") {
      firePageView();
    } else {
      window.addEventListener("load", firePageView, { once: true });
    }

    return function cleanup() {
      cancelled = true;
      window.removeEventListener("load", firePageView);
    };
  }, [triggerKey]);
}

export { usePageViewAfterLoad };
